import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { apiKey } from "../../../config/config";

export default function CustomerFeedback() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    phone: "",
    answers: []
  });
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  // Fetch feedback form
  useEffect(() => {
    axios
      .get(`${apiKey}/api/feedback-customer-form`)
      .then((res) => {
        const data = res.data;
        setForm(data);
        setUserData((prev) => ({
          ...prev,
          answers: new Array(data.questions?.length || 0).fill("")
        }));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error loading feedback form", { theme: "colored" });
        setLoading(false);
      });
  }, []);

  const handleChange = (value, index) => {
    const updated = [...userData.answers];
    updated[index] = value;
    setUserData({ ...userData, answers: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const feedbackData = form.questions.map((q, index) => ({
      question: q.question,
      answer: userData.answers[index] || ""
    }));

    const payload = {
      name: userData.name,
      email: userData.email,
      phone: userData.phone,
      feedbacks: feedbackData
    };

    axios
      .post(`${apiKey}/api/feedback-form-submit`, payload)
      .then((res) => {
        setMessage(res.data.message);
        setShowModal(true);

        setUserData({
          name: "",
          email: "",
          phone: "",
          answers: new Array(form.questions.length).fill("")
        });
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Error submitting feedback";
        toast.error(msg, { theme: "colored" });
      });
  };

  if (loading)
    return <p className="text-center mt-6 text-gray-500">Loading...</p>;
  if (!form || !form.questions || !form.questions.length)
    return (
      <p className="text-center mt-6 text-gray-500">No feedback form found.</p>
    );

  return (
    <div className="max-w-3xl mx-auto my-10 p-8 bg-white shadow-xl rounded-3xl border border-gray-200">
      <h2 className="text-4xl font-bold mb-8 text-[#7B20C7] text-center">
        {form.title}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Info with Floating Labels */}
        {["name", "email", "phone"].map((field) => (
          <div className="relative" key={field}>
            <input
              type={field === "email" ? "email" : "text"}
              name={field}
              value={userData[field]}
              onChange={(e) =>
                setUserData({ ...userData, [field]: e.target.value })
              }
              placeholder=" "
              required
              className="peer w-full p-4 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-purple-500 focus:outline-none transition"
            />
            <label className="absolute left-4 top-3 text-gray-400 text-sm peer-placeholder-shown:top-4 peer-placeholder-shown:text-gray-500 peer-placeholder-shown:text-base transition-all font-medium">
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </label>
          </div>
        ))}

        {/* Feedback Questions */}
        <div>
          <h3 className="text-2xl font-semibold mb-6">Feedback Questions</h3>
          {form.questions.map((q, index) => {
            const options =
              q.options?.filter((opt) => opt && opt !== "null") || [];
            return (
              <div
                key={index}
                className="p-4 mb-6 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition bg-gray-50"
              >
                <label className="block mb-3 text-gray-800 font-semibold">
                  {q.question}
                </label>

                {q.multiOption && options.length > 1 ? (
                  <div className="flex flex-wrap gap-3">
                    {options.map((opt, oIndex) => (
                      <button
                        type="button"
                        key={oIndex}
                        onClick={() => handleChange(opt, index)}
                        className={`px-4 py-2 rounded-lg border transition font-medium ${
                          userData.answers[index] === opt
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-purple-100"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : (
                  <textarea
                    value={userData.answers[index] || ""}
                    onChange={(e) => handleChange(e.target.value, index)}
                    className="w-full p-4 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-purple-500 focus:outline-none transition resize-none"
                    placeholder="Type your answer here..."
                    rows={3}
                    required
                  />
                )}
              </div>
            );
          })}
        </div>

        <button
          type="submit"
          className="w-full p-4 bg-[#7B20C7] text-white rounded-2xl font-semibold text-lg hover:bg-[#5a178f] transition shadow-md hover:shadow-lg"
        >
          Submit Feedback
        </button>
      </form>

      {/* Success Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4 text-purple-600">
              {message}
            </h3>
            <button
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              onClick={() => setShowModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
